import { ApiError } from "@/types/api";
import { getToken, tryRefresh } from "./auth";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/atlas`;

function normalizeError(status: number | undefined, message: string): ApiError {
  if (status === 401) return { kind: "unauthorized", status, message: "unauthorized" };
  if (status === 403) return { kind: "forbidden", status, message: "forbidden" };
  if (status === 429) return { kind: "quota", status, message: "quota exceeded" };
  if (status === 404) return { kind: "notfound", status, message: "not found" };
  if (status === 504) return { kind: "timeout", status, message: "upstream timeout" };
  if (status === 502) return { kind: "network", status, message: "upstream unavailable" };
  if (status && status >= 500) return { kind: "server", status, message: "server error" };
  return { kind: "unknown", status, message };
}

export interface RequestOpts {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  auth?: boolean; // attach bearer token if present
  retries?: number;
  headers?: Record<string, string>; // extra request headers (e.g. Idempotency-Key)
  base?: string; // override API base (e.g. launch/billing surface at /api)
}

export async function apiRequest<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const { method = "GET", body, signal, auth = false, retries = 1, headers: extraHeaders, base = BASE } = opts;
  const headers: Record<string, string> = { "Content-Type": "application/json", ...(extraHeaders || {}) };
  if (auth) {
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  let attempt = 0;
  let refreshed = false;
  let lastErr: ApiError = { kind: "unknown", message: "unknown" };
  while (attempt <= retries) {
    try {
      const res = await fetch(`${base}${path}`, {
        method,
        headers,
        body: body != null ? JSON.stringify(body) : undefined,
        signal,
      });
      if (!res.ok) {
        // authed 401 -> attempt a single silent token refresh, then retry once
        if (res.status === 401 && auth && !refreshed) {
          refreshed = true;
          const ok = await tryRefresh();
          if (ok) {
            const t = getToken();
            if (t) headers["Authorization"] = `Bearer ${t}`;
            continue;
          }
        }
        // do not silently swap production errors for mock data
        if ((res.status >= 500 || res.status === 502 || res.status === 504) && attempt < retries) {
          attempt++;
          await new Promise((r) => setTimeout(r, 400 * attempt));
          continue;
        }
        throw normalizeError(res.status, `HTTP ${res.status}`);
      }
      const ct = res.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        return (await res.text()) as unknown as T;
      }
      return (await res.json()) as T;
    } catch (e: any) {
      if (e && typeof e === "object" && "kind" in e) {
        lastErr = e as ApiError;
        if (lastErr.kind !== "network" && lastErr.kind !== "timeout" && lastErr.kind !== "server") break;
      } else if (e?.name === "AbortError") {
        throw { kind: "unknown", message: "aborted" } as ApiError;
      } else {
        lastErr = { kind: "network", message: e?.message || "network error" };
      }
      if (attempt < retries) {
        attempt++;
        await new Promise((r) => setTimeout(r, 400 * attempt));
        continue;
      }
      break;
    }
  }
  throw lastErr;
}

export { BASE as API_BASE };
